import { OptionsObject, SnackbarKey } from 'notistack';
import { useSnackbar } from './use-snackbar.hooks';
import React, { useCallback } from 'react';
import { Button } from '@ui/buttons';
import { useUndo, useWaitForUndoStack } from './use-undo-redo.hooks';

export type SnackbarWithUndoOptions = OptionsObject & {
  withUndo?: boolean;
  /**
   * When `withUndo` is true: runs after the stack is ready, before global undo.
   * Return value is passed to {@link SnackbarWithUndoOptions.afterUndo}.
   */
  beforeUndo?: () => unknown | Promise<unknown>;
  /**
   * When `withUndo` is true: runs after global undo completes.
   * @param context - return value from {@link SnackbarWithUndoOptions.beforeUndo}
   */
  afterUndo?: (context: unknown) => void | Promise<void>;
  /**
   * When `withUndo` is true, wait for temporal undo history (default `true`).
   */
  waitForUndoStack?: boolean;
};

const UNDO_EXTRA_DURATION_MS = 5000;

const UndoSnackbarAction = ({
  snackbarId,
  onUndo,
}: {
  snackbarId: SnackbarKey;
  onUndo: (id: SnackbarKey) => void | Promise<void>;
}) =>
  React.createElement(Button, {
    variant: 'text',
    size: 'small',
    // Keep an open inline rename from committing on blur before this click runs
    'data-keep-inline-edit': true,
    onClick: () => {
      void onUndo(snackbarId);
    },
    children: 'Undo',
  });

/**
 * Enqueues a snackbar with an optional "Undo" action (global undo + optional before/after hooks).
 * When `withUndo` is true the snackbar stays open 5 s longer by default and awaits stack readiness
 * before undo (see {@link useWaitForUndoStack}).
 */
export const useSnackbarWithUndo = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const undo = useUndo();
  const waitForUndoStack = useWaitForUndoStack();

  return useCallback(
    (
      message: string,
      {
        withUndo,
        autoHideDuration,
        beforeUndo,
        afterUndo,
        waitForUndoStack: shouldWaitForStack = true,
        ...rest
      }: SnackbarWithUndoOptions = {},
    ) => {
      const duration =
        (autoHideDuration ?? 3000) + (withUndo ? UNDO_EXTRA_DURATION_MS : 0);

      return enqueueSnackbar(message, {
        ...rest,
        autoHideDuration: duration,
        ...(withUndo && {
          action: (id: SnackbarKey) =>
            React.createElement(UndoSnackbarAction, {
              snackbarId: id,
              onUndo: async (snackbarId) => {
                const context = await beforeUndo?.();
                if (shouldWaitForStack) await waitForUndoStack();
                await undo();
                await afterUndo?.(context);
                closeSnackbar(snackbarId);
              },
            }),
        }),
      });
    },
    [enqueueSnackbar, closeSnackbar, undo, waitForUndoStack],
  );
};
